"use client";

/**
 * /scope — "Scope My Build" agent.
 * A futuristic command-deck: describe an idea, watch the agent stream its
 * thinking and assemble a live, wiring-itself build spec. Talks to /api/scope
 * (live Claude if ANTHROPIC_API_KEY is set, graceful demo mode otherwise).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { track } from "@vercel/analytics";
import { motion, AnimatePresence } from "framer-motion";
import { PROJECTS } from "@/data/projects";

const SPEC_DELIM = "===SPEC===";
const EMERALD = "#10B981";
const AMBER = "#D4A843";

type Layer = "Frontend" | "Backend" | "AI" | "Payments" | "Mobile" | "Hosting";
interface Spec {
  projectType: string;
  summary: string;
  stack: { layer: Layer; choice: string; why: string }[];
  features: { name: string; phase: 1 | 2 | 3 }[];
  phases: { name: string; weeks: string; deliverable: string }[];
  match: { slug: string; name: string; similarity: number; reason: string };
  ballpark: { weeks: string; complexity: "Low" | "Medium" | "High" };
}

const SAMPLES = [
  "An AI app that turns my fridge photo into dinner recipes",
  "A marketplace where contractors send instant quotes",
  "A voice receptionist that books appointments 24/7",
  "A habit tracker with a smart coach that texts me",
];

export default function ScopePage() {
  const [idea, setIdea] = useState("");
  const [phase, setPhase] = useState<"idle" | "booting" | "streaming" | "done" | "cooldown">("idle");
  const [analysis, setAnalysis] = useState("");
  const [spec, setSpec] = useState<Spec | null>(null);
  const [rest, setRest] = useState<{ msg: string; secs: number }>({ msg: "", secs: 0 });

  const run = useCallback(async (text: string) => {
    if (!text.trim() || phase === "streaming" || phase === "booting" || phase === "cooldown") return;
    // Usage analytics — track each scope run (length bucket, not the raw idea).
    track("scope_run", { length: text.trim().length });
    setAnalysis("");
    setSpec(null);
    setPhase("booting");
    await new Promise((r) => setTimeout(r, 700)); // HUD boot beat
    setPhase("streaming");

    try {
      const res = await fetch("/api/scope", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ idea: text }),
      });

      // FORGE is recovering — themed cooldown, not a cold "rate limited" error.
      if (res.status === 429) {
        const secs = Number(res.headers.get("X-Scope-Cooldown") || "30");
        const msg = await res.text();
        setRest({ msg, secs });
        setPhase("cooldown");
        return;
      }

      if (!res.body) throw new Error("no stream");

      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let full = "";
      let specStarted = false;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        full += dec.decode(value, { stream: true });
        const di = full.indexOf(SPEC_DELIM);
        if (di === -1) {
          setAnalysis(full);
        } else {
          if (!specStarted) {
            specStarted = true;
            setAnalysis(full.slice(0, di).trim());
          }
        }
      }
      const di = full.indexOf(SPEC_DELIM);
      if (di !== -1) {
        const json = full.slice(di + SPEC_DELIM.length).trim();
        try {
          setSpec(JSON.parse(json) as Spec);
        } catch {
          /* leave spec null; analysis still shows */
        }
      }
      setPhase("done");
    } catch {
      setPhase("done");
      if (!analysis) setAnalysis("Signal lost. Try again in a moment.");
    }
  }, [phase, analysis]);

  // Tick the cooldown down; release FORGE when rested.
  useEffect(() => {
    if (phase !== "cooldown") return;
    const id = setTimeout(() => {
      if (rest.secs <= 1) setPhase("idle");
      else setRest((r) => ({ ...r, secs: r.secs - 1 }));
    }, 1000);
    return () => clearTimeout(id);
  }, [phase, rest.secs]);

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#06060B] text-[#FAFAFA]">
      <NeuralField active={phase !== "idle"} />
      <GridFloor />
      <HudFrame />

      <Link
        href="/"
        className="absolute left-6 top-6 z-20 rounded-full border border-white/10 bg-black/40 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 backdrop-blur-sm transition hover:border-emerald-500/40 hover:text-emerald-300"
      >
        ← Back to site
      </Link>

      <div className="relative z-10 mx-auto max-w-5xl px-5 pb-32 pt-16 sm:pt-24">
        <header className="mb-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3 inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/5 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-300/80"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
            MDG Labs · FORGE build agent
          </motion.div>
          <h1
            className="bg-gradient-to-b from-white to-white/55 bg-clip-text text-4xl font-bold tracking-tight text-transparent sm:text-6xl"
            style={{ fontFamily: "var(--font-space-grotesk), system-ui" }}
          >
            Describe it. Watch it get built.
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm text-slate-400 sm:text-base">
            Tell the agent your idea. It scopes the stack, the features, the
            timeline — and shows you which of Micah&apos;s shipped products it
            most resembles. Live.
          </p>
        </header>

        <Console
          idea={idea}
          setIdea={setIdea}
          onRun={() => run(idea)}
          busy={phase === "streaming" || phase === "booting"}
        />

        <AnimatePresence>
          {phase === "idle" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-5 flex flex-wrap justify-center gap-2"
            >
              {SAMPLES.map((s) => (
                <button
                  key={s}
                  onClick={() => { setIdea(s); run(s); }}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono text-[11px] text-slate-400 transition hover:border-emerald-500/40 hover:text-emerald-300"
                >
                  {s}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Boot sequence */}
        <AnimatePresence>
          {phase === "booting" && <BootSequence />}
        </AnimatePresence>

        {/* FORGE recovering (hidden rate limit) */}
        <AnimatePresence>
          {phase === "cooldown" && <CooldownPanel msg={rest.msg} secs={rest.secs} />}
        </AnimatePresence>

        {/* Streamed analysis terminal */}
        <AnimatePresence>
          {(phase === "streaming" || phase === "done") && analysis && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 rounded-xl border border-white/10 bg-black/40 p-5 backdrop-blur-sm"
              style={{ boxShadow: `0 0 60px -20px ${EMERALD}55` }}
            >
              <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-emerald-400/70">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                FORGE · analyzing
              </div>
              <p className="font-mono text-sm leading-relaxed text-slate-200">
                {analysis}
                {phase === "streaming" && (
                  <span className="ml-0.5 inline-block h-4 w-2 translate-y-0.5 animate-pulse bg-emerald-400" />
                )}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* The build spec materializes */}
        <AnimatePresence>{spec && <SpecDeck spec={spec} />}</AnimatePresence>
      </div>
    </main>
  );
}

/* ------------------------------- Console -------------------------------- */

function Console({
  idea, setIdea, onRun, busy,
}: { idea: string; setIdea: (v: string) => void; onRun: () => void; busy: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="group relative mx-auto max-w-2xl"
    >
      <div
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-60 blur-md transition group-focus-within:opacity-100"
        style={{ background: `linear-gradient(120deg, ${EMERALD}, ${AMBER})` }}
      />
      <div className="relative rounded-2xl border border-white/10 bg-[#0A0A12]/90 p-2 backdrop-blur-md">
        <textarea
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          onKeyDown={(e) => {
            // Enter runs it; Shift+Enter (or the textarea default) adds a newline.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onRun();
            }
          }}
          rows={2}
          placeholder="Describe the app or site you want built…"
          className="w-full resize-none bg-transparent px-4 py-3 font-mono text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none"
        />
        <div className="flex items-center justify-between px-3 pb-1">
          <span className="font-mono text-[11px] text-slate-600">↵ to run · ⇧↵ newline</span>
          <button
            onClick={onRun}
            disabled={busy}
            className="relative overflow-hidden rounded-lg px-5 py-2 font-mono text-sm font-medium text-black transition disabled:opacity-50"
            style={{ background: `linear-gradient(120deg, ${EMERALD}, ${AMBER})` }}
          >
            {busy ? "scoping…" : "Scope it ⚡"}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ----------------------------- Boot sequence ---------------------------- */

function BootSequence() {
  const lines = [
    "initializing build agent…",
    "loading portfolio index · 19 products",
    "calibrating stack heuristics",
    "establishing uplink",
  ];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="mx-auto mt-8 max-w-2xl rounded-xl border border-emerald-500/20 bg-black/40 p-5 font-mono text-xs text-emerald-300/80"
    >
      {lines.map((l, i) => (
        <motion.div
          key={l}
          initial={{ opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.14 }}
          className="flex items-center gap-2 py-0.5"
        >
          <span className="text-emerald-500">›</span> {l}
          <span className="ml-auto text-emerald-500/60">ok</span>
        </motion.div>
      ))}
    </motion.div>
  );
}

/* ---------------------- FORGE recovering (cooldown) --------------------- */

function CooldownPanel({ msg, secs }: { msg: string; secs: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="mx-auto mt-8 max-w-2xl overflow-hidden rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.08] to-transparent p-6 text-center"
    >
      {/* Resting ember — pulses slowly while FORGE recovers */}
      <motion.div
        animate={{ scale: [1, 1.12, 1], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        className="mx-auto mb-3 h-10 w-10 rounded-full"
        style={{ background: "radial-gradient(circle, #D4A843, #7c5e1e)", boxShadow: "0 0 30px #D4A84388" }}
      />
      <div className="font-mono text-[11px] uppercase tracking-widest text-amber-300/70">
        FORGE · recovering
      </div>
      <p className="mx-auto mt-2 max-w-md text-sm text-amber-100/90">{msg}</p>
      <div className="mt-3 font-mono text-2xl font-semibold text-amber-300">
        {secs}s
      </div>
      <div className="mx-auto mt-2 h-1 w-40 overflow-hidden rounded-full bg-white/10">
        <motion.div
          className="h-full bg-amber-400"
          initial={{ width: "100%" }}
          animate={{ width: "0%" }}
          transition={{ duration: secs, ease: "linear" }}
        />
      </div>
    </motion.div>
  );
}

/* ------------------------------- Spec deck ------------------------------ */

function SpecDeck({ spec }: { spec: Spec }) {
  const matched = useMemo(
    () => PROJECTS.find((p) => p.slug === spec.match.slug) || null,
    [spec.match.slug]
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="mt-8 space-y-6"
    >
      {/* Headline stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Type" value={spec.projectType} />
        <Stat label="Timeline" value={spec.ballpark.weeks} accent />
        <Stat label="Complexity" value={spec.ballpark.complexity} />
        <SimilarityStat value={spec.match.similarity} name={spec.match.name} />
      </div>

      {/* Stack — wiring itself together */}
      <Panel title="Recommended stack" sub="how I'd build it">
        <div className="grid gap-2 sm:grid-cols-2">
          {spec.stack.map((s, i) => (
            <motion.div
              key={s.layer + s.choice}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.06 * i }}
              className="flex items-start gap-3 rounded-lg border border-white/8 bg-white/[0.02] p-3"
            >
              <span className="mt-0.5 rounded bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-300">
                {s.layer}
              </span>
              <div>
                <div className="text-sm font-medium text-slate-100">{s.choice}</div>
                <div className="text-xs text-slate-500">{s.why}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </Panel>

      {/* Features by phase */}
      <Panel title="Feature map" sub="phased to ship value early">
        <div className="grid gap-4 sm:grid-cols-3">
          {[1, 2, 3].map((ph) => (
            <div key={ph}>
              <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-amber-300/70">
                Phase {ph}
              </div>
              <div className="space-y-2">
                {spec.features.filter((f) => f.phase === ph).map((f, i) => (
                  <motion.div
                    key={f.name}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 + ph * 0.1 + i * 0.05 }}
                    className="rounded-lg border border-white/8 bg-gradient-to-br from-white/[0.04] to-transparent p-2.5 text-xs text-slate-200"
                  >
                    {f.name}
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Timeline */}
      <Panel title="Build timeline" sub={spec.ballpark.weeks}>
        <div className="space-y-3">
          {spec.phases.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 + i * 0.12 }}
              className="relative pl-6"
            >
              <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-4 ring-emerald-500/15" />
              {i < spec.phases.length - 1 && (
                <span className="absolute left-[5px] top-4 h-full w-px bg-gradient-to-b from-emerald-500/40 to-transparent" />
              )}
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-sm font-medium text-slate-100">{p.name}</span>
                <span className="font-mono text-[11px] text-emerald-300/70">{p.weeks}</span>
              </div>
              <div className="text-xs text-slate-500">{p.deliverable}</div>
            </motion.div>
          ))}
        </div>
      </Panel>

      {/* Closest match in portfolio */}
      {matched && (
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="overflow-hidden rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.07] to-transparent p-5"
        >
          <div className="mb-1 font-mono text-[11px] uppercase tracking-widest text-emerald-300/70">
            Closest thing I&apos;ve shipped · {spec.match.similarity}% match
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xl font-semibold text-white">{matched.name}</div>
              <div className="max-w-md text-sm text-slate-400">{matched.description}</div>
              <div className="mt-1 font-mono text-xs text-emerald-300/60">{spec.match.reason}</div>
            </div>
            <Link
              href={`/work/${matched.slug}`}
              className="shrink-0 rounded-lg border border-emerald-500/30 px-4 py-2 text-sm text-emerald-300 transition hover:bg-emerald-500/10"
            >
              See the case study →
            </Link>
          </div>
        </motion.div>
      )}

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.75 }}
        className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-center"
      >
        <div className="text-lg font-medium text-white">Want Micah to actually build this?</div>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-400">
          This scope took seconds. Imagine the product. Let&apos;s talk timeline and budget.
        </p>
        <Link
          href="/hire"
          className="mt-4 inline-block rounded-lg px-6 py-2.5 font-medium text-black"
          style={{ background: `linear-gradient(120deg, ${EMERALD}, ${AMBER})` }}
        >
          Start the conversation →
        </Link>
      </motion.div>
    </motion.div>
  );
}

/* -------------------------------- Atoms --------------------------------- */

function Panel({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-sm"
    >
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold tracking-wide text-white">{title}</h3>
        {sub && <span className="font-mono text-[11px] text-slate-500">{sub}</span>}
      </div>
      {children}
    </motion.section>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 truncate text-sm font-semibold ${accent ? "text-amber-300" : "text-slate-100"}`}>
        {value}
      </div>
    </motion.div>
  );
}

function SimilarityStat({ value, name }: { value: number; name: string }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / 900);
      setN(Math.round(p * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3"
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400/70">Match · {name}</div>
      <div className="mt-1 text-sm font-semibold text-emerald-300">{n}%</div>
    </motion.div>
  );
}

/* --------------------------- Ambient visuals ---------------------------- */

function NeuralField({ active }: { active: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let raf = 0;
    let w = 0, h = 0;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const N = 54;
    const pts = Array.from({ length: N }, () => ({
      x: Math.random(), y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0006,
      vy: (Math.random() - 0.5) * 0.0006,
    }));
    const resize = () => {
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const speed = active ? 2.2 : 1;
      for (const p of pts) {
        p.x += p.vx * speed; p.y += p.vy * speed;
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;
      }
      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const a = pts[i], b = pts[j];
          const dx = (a.x - b.x) * w, dy = (a.y - b.y) * h;
          const d = Math.hypot(dx, dy);
          if (d < 130) {
            ctx.strokeStyle = `rgba(16,185,129,${(1 - d / 130) * (active ? 0.22 : 0.12)})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x * w, a.y * h);
            ctx.lineTo(b.x * w, b.y * h);
            ctx.stroke();
          }
        }
      }
      for (const p of pts) {
        ctx.fillStyle = `rgba(16,185,129,${active ? 0.55 : 0.3})`;
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [active]);
  return <canvas ref={ref} className="pointer-events-none absolute inset-0 h-full w-full opacity-70" />;
}

function GridFloor() {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2"
      style={{
        backgroundImage:
          "linear-gradient(rgba(16,185,129,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.08) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
        maskImage: "linear-gradient(to top, black, transparent)",
        WebkitMaskImage: "linear-gradient(to top, black, transparent)",
        transform: "perspective(420px) rotateX(58deg)",
        transformOrigin: "bottom",
        opacity: 0.5,
      }}
    />
  );
}

function HudFrame() {
  const c = "absolute h-8 w-8 border-emerald-500/30";
  return (
    <div className="pointer-events-none absolute inset-3 z-20">
      <div className={`${c} left-0 top-0 border-l-2 border-t-2`} />
      <div className={`${c} right-0 top-0 border-r-2 border-t-2`} />
      <div className={`${c} bottom-0 left-0 border-b-2 border-l-2`} />
      <div className={`${c} bottom-0 right-0 border-b-2 border-r-2`} />
    </div>
  );
}
